/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { OptionType } from '../../../../util/types';
import { useI18n } from '@whizz/react-i18n';
import { repository } from '@whizz/api-client';
import { useQuery } from 'react-query';
import { useLocalStorage } from 'react-use';
import { Button, ButtonIcon, ChoiceGroup, Selector, Table } from '../../../../components';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import Input from '../../../../components/input/Input';
import Loader from '../../../../components/loader/Loader';
import { DateTime } from 'luxon';
import { getNextWeekday } from '../../../../util/dates';
import ChangePausedUntilModal from '../../WeeklyLeadersEmails/modules/ChangePausedUntilModal';
import { createGlobalStyle } from 'styled-components';
import { throttle } from 'lodash';

const GlobalStyle = createGlobalStyle`
  table{
    .leader-title,
    [data-id='col_next_send_date'],
    [data-column='cell_next_send_date'] {
      margin-left: 10px;
      position: relative;
      background: inherit;
      z-index: 2;
      &:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -14px;
        bottom: -14px;
        z-index: -1;
        pointer-events: none;
        box-shadow: -14px 0 14px -14px rgba(0,0,0,.35);
      }
    }
    .leader-title,
    [data-id='col_change_next_send_date'],
    [data-column='cell_change_next_send_date'] {
      position: relative;
      background: inherit;
      z-index: 2;
      &:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -14px;
        bottom: -14px;
        z-index: -1;
        pointer-events: none;
        box-shadow: 14px 0 14px -14px rgba(0,0,0,.35);
      }
    }
  }
`;

const SchoolsTable = ({ onClickEdit }: Props) => {
  const { translate: t } = useI18n();

  const [ selectedSchools, setSelectedSchools ] = useState<number[]>([]);
  const [ search, setSearch ] = useLocalStorage<string>('admin_search', '');
  const [ filterExtraData, setExtraData ] = useLocalStorage<{ country: OptionType<string> | null; }>('admin_filter', { country: null });
  const [ pageIndex, setPageIndex ] = useLocalStorage<number>('admin_page_index', 0);
  const [ modalsData, setModalsData ] = useState<ModalsData>({ action: null });

  const safeFilterExtraData = filterExtraData ?? { country: null };
  const safeSetExtraData = setExtraData ?? (() => { console.warn('setExtraData is undefined'); });
  const safeSetPageIndex = useMemo(
    () => setPageIndex ?? (() => { console.warn('setPageIndex is undefined'); }),
    [ setPageIndex ],
  );
  const throttledResetPageIndex = useMemo(
    () => throttle(() => safeSetPageIndex(0), 500),
    [ safeSetPageIndex ],
  );
  const hasMounted = useRef(false);

  const getSchools = useQuery({
    queryKey: QUERIES_IDS.ADMIN.GET_SCHOOLS,
    queryFn: () => repository.AdminRepository.getSchools(),
  });

  function filteredSchools() {
    const schools = getSchools.data?.data ?? [];

    const byCountry = schools.filter((school) => {
      if (null != safeFilterExtraData.country?.value) {
        return school.school.country === safeFilterExtraData.country.value;
      }

      return true;
    });

    if (null == search || '' === search) {
      return byCountry;
    }

    const lowercasedSearch = search.toLowerCase();

    const matches = byCountry.filter((school) => {
      const { school: { name, city, postcode } } = school;
      const foundInName = name.toLowerCase().includes(lowercasedSearch);
      const foundInCity = city?.toLowerCase().includes(lowercasedSearch);
      const foundInPostcode = postcode?.toLowerCase().includes(lowercasedSearch);

      return foundInName || foundInCity || foundInPostcode;
    });

    return matches.sort((left, right) => {
      const leftName = left.school.name.toLowerCase();
      const rightName = right.school.name.toLowerCase();
      const leftStarts = leftName.startsWith(lowercasedSearch);
      const rightStarts = rightName.startsWith(lowercasedSearch);

      if (leftStarts !== rightStarts) {
        return leftStarts ? -1 : 1;
      }

      return leftName.localeCompare(rightName);
    });
  }

  function modals() {
    if (null === modalsData.action) {
      return null;
    }

    return (
      <ChangePausedUntilModal
        toggle={() => setModalsData({ action: null })}
        schools={modalsData.schools}
        onSuccess={() => setSelectedSchools([])}
      />
    );
  }

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return undefined;
    }
    throttledResetPageIndex();
    return () => throttledResetPageIndex.cancel();
  }, [ search, safeFilterExtraData.country?.value ]);

  if (getSchools.isLoading) {
    return (
      <Loader />
    );
  }

  const countries = Array.from(new Set(
    (getSchools.data?.data ?? [])
      .map((school) => school.school.country)
      .filter((country): country is string => null != country),
  ));

  const priorityCountries = [ 'GB', 'US', 'NZ', 'TH', 'KE' ];

  const sortedCountries = [
    ...priorityCountries.filter((country) => countries.includes(country)),
    ...countries
      .filter((country) => !priorityCountries.includes(country))
      .sort(),
  ];

  const countryOptions: OptionType[] = sortedCountries.map((country) => ({
    label: t(`countries.COUNTRY_${country}`),
    value: country,
  }));

  const schools = filteredSchools();
  const schoolsWithNextSendDate = schools.filter((school) => school.isEnabled);
  return (
    <>
      {modals()}
      <div className={'flex flex-col gap-5'}>
        <GlobalStyle />
        <Table
          id={'weekly_leaders_emails'}
          customHeaderComponent={<Filters options={countryOptions} search={search} country={safeFilterExtraData.country} handleSearch={setSearch} handleChange={safeSetExtraData} />}
          cols={[
            {
              id: 'edit',
              Header: t('pages.ADMIN.TABLE.HEADERS.EDIT')!,
              accessor: 'school.id',
              Cell: (props) => (
                <ButtonIcon
                  icon={GLOBAL_ICONS.edit}
                  size={'sm'}
                  isOutline
                  onClick={() => onClickEdit(props.row.original.school.id)}
                />
              ),
              disableSortBy: true,
            },
            {
              id: 'id',
              Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_ID')!,
              accessor: 'school.id',
            },
            {
              Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_NAME')!,
              accessor: 'school.name',
            },
            {
              Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_TOWN')!,
              accessor: 'school.city',
            },
            {
              Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_POSTCODE')!,
              accessor: 'school.postcode',
            },
            {
              Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_COUNTRY')!,
              id: 'country',
              accessor: (original) => t(`countries.COUNTRY_${original.school.country}`),
            },
            {
              id: 'next_send_date',
              Header: () => (
                <Button
                  isOutline
                  onClick={() => setModalsData({
                    action: ModalsAction.changeNextSendData,
                    schools: selectedSchools,
                  })}
                  size={'sm'}
                  disabled={0 === selectedSchools.length}
                >
                  {t('pages.ADMIN.TABLE.HEADERS.CHANGE_NEXT_SEND_DATE')}
                </Button>
              ),
              accessor: (original) => {
                if (!original.isEnabled) {
                  return null;
                }

                const now = DateTime.now().setZone(original.school.timezone);

                let dateFrom = now;

                if (null != original.pausedUntil) {
                  const pausedUntil = DateTime.fromISO(original.pausedUntil);

                  if (now < pausedUntil) {
                    dateFrom = pausedUntil;
                  }
                }

                const nextWeekday = getNextWeekday(original.weekday, original.hour, dateFrom);

                return nextWeekday.toLocaleString();
              },
              Cell: ({ cell }) => {
                if (null === cell.value) {
                  return (
                    <span className={'text-muted italic'}>
                        Disabled
                    </span>
                  );
                }

                return cell.value;
              },
            },
            {
              Header: () => (
                <div className='flex justify-center'>
                  <ChoiceGroup
                    id={'change_all'}
                    label={''}
                    hideLabel
                    checked={selectedSchools.length === schoolsWithNextSendDate.length && schoolsWithNextSendDate.length > 0}
                    onChange={() => setSelectedSchools((prev) => {
                      if (prev.length === schoolsWithNextSendDate.length) {
                        return [];
                      }
                      const allSchoolIds = schoolsWithNextSendDate.map((school) => school.school.id);
                      return allSchoolIds;
                    })}
                    disabled={null == safeFilterExtraData.country?.value}
                  />
                </div>
              ),
              id: 'change_next_send_date',
              accessor: 'id',
              Cell: ({ cell, row }) => {
                const original = row.original;
                const schoolId = original.school.id;

                return (
                  <div className='flex justify-center'>
                    <ChoiceGroup
                      id={`change_all_none_${schoolId}`}
                      label={''}
                      hideLabel
                      checked={selectedSchools.includes(schoolId)}
                      onChange={() => setSelectedSchools((prev) => {
                        if (prev.includes(schoolId)) {
                          return prev.filter((id) => id !== schoolId);
                        }

                        return [ ...prev, schoolId ];
                      })}
                      disabled={null === cell.value || !original.isEnabled}
                    />
                  </div>
                );
              },
              disableSortBy: true,
            },
          ]}
          content={schools}
          noDataMessage={t('pages.ADMIN.TABLE.NO_DATA')}
          initialState={{
            pageSize: 50,
            pageIndex: pageIndex ?? 0,
          }}
          onPageIndexChange={setPageIndex}
        />
      </div>
    </>
  );
};

const Filters = ({ options, search, country, handleSearch, handleChange }: { options: OptionType[]; search: string; country: OptionType<string> | null; handleSearch: (value: string) => void; handleChange: (value: (prev: { country: OptionType<string> | null }) => { country: OptionType<string> | null }) => void; }) => {
  const { translate } = useI18n();
  return (
    <tr>
      <th colSpan={5}>
        <Input
          id={'search'}
          label={'Search'}
          hideLabel
          placeholder={'Search school, town or postcode'}
          onChange={(event) => handleSearch(event.target.value)}
          value={search}
        />
      </th>
      <th>
        <div className={'w-48 mr-5'}>
          <CountrySelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.YEAR_GROUP')}
            placeholder={translate('components.SELECTORS.PLACEHOLDERS.FILTER')}
            options={options}
            value={country}
            onChange={(option) => handleChange((prev) => ({ ...prev, country: option }))}
            isClearable
            hideLabel
          />
        </div>
      </th>
      <th colSpan={2} className={'leader-title border-b-4 border-header text-center'}>
        <div className={'font-bold not-italic'}>{translate('pages.ADMIN.LEADERS_EMAILS.TITLE')}</div></th>
    </tr>
  );
};

const CountrySelector = (
  {
    label,
    options,
    value,
    hideLabel,
    placeholder,
    onChange,
  }: SelectorProps & { options: OptionType[] }) => {
  const { translate } = useI18n();
  return (
    <Selector
      id={'country'}
      label={label}
      placeholder={placeholder ?? translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SELECT_CLASS')}
      options={options}
      value={value}
      hideLabel={hideLabel}
      onChange={(selectedOption: OptionType<string> | null) => onChange(selectedOption ?? null)}
      size={'sm'}
    />
  );
};

interface SelectorProps {
  label: string;
  value: OptionType<string> | null;
  hideLabel?: boolean;
  placeholder?: string;
  onChange: (value: OptionType<string> | null) => void;
}

interface Props {
  onClickEdit: (schoolId: number) => void;
}

enum ModalsAction {
  changeNextSendData,
}

type ModalsData = {
  action: ModalsAction.changeNextSendData;
  schools: number[];
} | { action: null };

export { SchoolsTable };
